import { getMap, setLocationMarker } from './map.js';
import { getAndStoreUserLocation } from '../utils/location-utils.js';
import { openSmartSharePopup, openSharePopup } from '../utils/share.js';
import { getText } from '../translations/translations.js';
import { UI_VISIBILITY, MAP_DYNAMIC_ACTION_BUTTONS, MARKER_POPUP_ACTION_BUTTONS } from '/settings.js';
import { toggleFavorite, isFavorite } from '../utils/favorites.js';
import { showDirectionsPopup } from '../utils/directions.js';
import { createIconElement } from '../utils/icon.js';
import { getSpot } from '../utils/cache/spots-cache.js';

// DOM Elements (reassignable because we may create them dynamically)
let zoomInBtn = document.getElementById('zoom-in');
let zoomOutBtn = document.getElementById('zoom-out');
let locationBtn = document.getElementById('location');
let loadingIndicator = document.getElementById('loading');
let shareBtn = document.getElementById('share');
let mapControlsGroup = document.querySelector('.map-controls-group');

// Central config for controls (single source of truth)
const CONTROLS_CONFIG = [
    {
        id: 'zoom',
        className: 'zoom-controls',
        buttons: ['zoom-in', 'zoom-out']
    },
    {
        id: 'map-actions',
        className: 'share-location-control',
        buttons: ['share', 'location']
    },
    {
        id: 'spot-actions',
        className: 'spot-actions-control',
        buttons: ['spot-share', 'spot-favorite', 'spot-directions', 'spot-url-1', 'spot-url-2']
    }
];

// Helper: create or return existing button element by id
function ensureButtonElement(buttonId, options = {}) {
    let btn = document.getElementById(buttonId);
    if (btn) return btn;
    btn = document.createElement('button');
    btn.id = buttonId;
    btn.className = options.className || 'icon-btn';
    if (options.iconId) {
        btn.appendChild(createIconElement(options.iconId, 'icon'));
    }
    return btn;
}

// Ensure group container exists and contains expected buttons (static ones)
function ensureControlsFromConfig() {
    if (!mapControlsGroup) return;
    CONTROLS_CONFIG.forEach(group => {
        // Let dynamic spot-actions be created by ensureSpotActions()
        if (group.id === 'spot-actions') return;
        let groupEl = mapControlsGroup.querySelector(`.${group.className}`);
        if (!groupEl) {
            groupEl = document.createElement('div');
            groupEl.className = group.className;
            mapControlsGroup.appendChild(groupEl);
        }
        group.buttons.forEach(btnId => {
            // only create static/map-level buttons here
            const existing = document.getElementById(btnId);
            if (!existing && (btnId === 'zoom-in' || btnId === 'zoom-out' || btnId === 'share' || btnId === 'location')) {
                const btn = ensureButtonElement(btnId, { className: (btnId === 'zoom-in' || btnId === 'zoom-out') ? '' : 'icon-btn' });
                // Add default icons for icon-btns if needed
                if ((btnId === 'share' || btnId === 'location') && !btn.querySelector('.icon')) {
                    const iconId = btnId === 'share' ? 'share-nodes-solid' : 'location-crosshairs-solid';
                    btn.appendChild(createIconElement(iconId, 'icon'));
                }
                // For zoom buttons, keep textual '+' and '−' if not icon
                if ((btnId === 'zoom-in' || btnId === 'zoom-out') && !btn.textContent) {
                    btn.textContent = btnId === 'zoom-in' ? '+' : '−';
                }
                groupEl.appendChild(btn);
            }
        });
    });
}

// Exposed API: set a button visible/hidden and update its parent group visibility
export function setButtonVisible(buttonId, visible) {
    const btn = document.getElementById(buttonId);
    if (!btn) return;
    btn.style.display = visible ? '' : 'none';
    // If the button is inside a group, update the group's visibility
    const parent = btn.parentElement;
    if (parent && parent.classList.contains('map-controls-group') === false) {
        // check if parent has any visible buttons
        const anyVisible = Array.from(parent.querySelectorAll('button')).some(b => b.style.display !== 'none');
        parent.style.display = anyVisible ? '' : 'none';
    }
}

export function setGroupVisible(groupClass, visible) {
    if (!mapControlsGroup) return;
    const group = mapControlsGroup.querySelector(`.${groupClass}`);
    if (group) group.style.display = visible ? '' : 'none';
}

// Dynamic spot action buttons (created lazily)
let spotActionsContainer = null;
let spotShareBtn = null;
let spotFavBtn = null;
let spotDirectionsBtn = null;
let spotUrlBtn1 = null;
let spotUrlBtn2 = null;
let currentSpot = null;

// Helper function to check if device is mobile
function isMobile() {
    return window.innerWidth <= 768;
}

function hasLatLng(spot) {
    const lat = spot?.coordinates?.latitude ?? spot?.coords_lat;
    const lng = spot?.coordinates?.longitude ?? spot?.coords_lng;
    if (typeof lat === 'number' && typeof lng === 'number') {
        return { lat, lng };
    }
    return null;
}

function isDynamicButtonEnabled(key) {
    // Support both 'favorite' and legacy 'heart'
    const keysToCheck = (key === 'favorite' || key === 'heart') ? ['favorite', 'heart'] : [key];

    const dynamicAllowed = keysToCheck
        .map(k => MAP_DYNAMIC_ACTION_BUTTONS?.BUTTONS?.[k])
        .find(v => typeof v === 'boolean');
    if (typeof dynamicAllowed === 'boolean') return dynamicAllowed;

    const popupAllowed = keysToCheck
        .map(k => MARKER_POPUP_ACTION_BUTTONS?.[k])
        .find(v => typeof v === 'boolean');
    return !!popupAllowed;
}

function ensureSpotActions() {
    if (!MAP_DYNAMIC_ACTION_BUTTONS?.ENABLED) return;
    if (spotActionsContainer || !mapControlsGroup) return;

    spotActionsContainer = document.createElement('div');
    spotActionsContainer.className = 'spot-actions-control';

    spotShareBtn = document.createElement('button');
    spotShareBtn.id = 'spot-share';
    spotShareBtn.className = 'icon-btn';
    spotShareBtn.appendChild(createIconElement('share-nodes-solid', 'icon'));

    spotFavBtn = document.createElement('button');
    spotFavBtn.id = 'spot-favorite';
    spotFavBtn.className = 'icon-btn';
    spotFavBtn.appendChild(createIconElement('star-solid-full', 'icon'));

    spotDirectionsBtn = document.createElement('button');
    spotDirectionsBtn.id = 'spot-directions';
    spotDirectionsBtn.className = 'icon-btn';
    spotDirectionsBtn.appendChild(createIconElement('map-location-dot-solid-full', 'icon'));

    // Dynamic URL buttons
    spotUrlBtn1 = document.createElement('button');
    spotUrlBtn1.id = 'spot-url-1';
    spotUrlBtn1.className = 'icon-btn spot-url-btn';
    spotUrlBtn2 = document.createElement('button');
    spotUrlBtn2.id = 'spot-url-2';
    spotUrlBtn2.className = 'icon-btn spot-url-btn';

    spotActionsContainer.append(spotShareBtn, spotFavBtn, spotDirectionsBtn, spotUrlBtn1, spotUrlBtn2);
    spotActionsContainer.style.display = 'none';

    mapControlsGroup.appendChild(spotActionsContainer);

    // Event bindings
    spotShareBtn.addEventListener('click', handleSpotShare);
    spotFavBtn.addEventListener('click', handleSpotFavorite);
    spotDirectionsBtn.addEventListener('click', handleSpotDirections);
    spotUrlBtn1.addEventListener('click', handleSpotUrl1);
    spotUrlBtn2.addEventListener('click', handleSpotUrl2);
}

// Helper function to apply button visibility based on settings
function applyButtonVisibility() {
    const mobile = isMobile();

    const dynamicEnabled = MAP_DYNAMIC_ACTION_BUTTONS?.ENABLED;
    const spotActionsEnabled = dynamicEnabled && currentSpot != null;

    // Apply zoom buttons visibility
    const zoomVisible = mobile ? UI_VISIBILITY.BUTTONS.ZOOM.MOBILE : UI_VISIBILITY.BUTTONS.ZOOM.DESKTOP;
    if (zoomInBtn) zoomInBtn.style.display = zoomVisible ? '' : 'none';
    if (zoomOutBtn) zoomOutBtn.style.display = zoomVisible ? '' : 'none';

    // Apply location button visibility
    const locationVisible = mobile ? UI_VISIBILITY.BUTTONS.LOCATION.MOBILE : UI_VISIBILITY.BUTTONS.LOCATION.DESKTOP;
    if (locationBtn) locationBtn.style.display = (!spotActionsEnabled && locationVisible) ? '' : 'none';

    // Apply share button visibility
    const shareVisible = mobile ? UI_VISIBILITY.BUTTONS.SHARE.MOBILE : UI_VISIBILITY.BUTTONS.SHARE.DESKTOP;
    if (shareBtn) shareBtn.style.display = (!spotActionsEnabled && shareVisible) ? '' : 'none';

    // Hide the entire share-location-control container when spot actions are enabled
    const shareLoc = mapControlsGroup?.querySelector('.share-location-control');
    if (shareLoc) {
        shareLoc.style.display = spotActionsEnabled ? 'none' : '';
    }

    // Toggle spot actions container
    if (spotActionsContainer) {
        // Determine which spot action buttons should render based on settings and data
        const allowShare = isDynamicButtonEnabled('share') && !!currentSpot?.id;
        // Respect spot-level share visibility flag (desktop/mobile). Defaults to true when flag missing.
        const spotShareSetting = mobile
            ? (UI_VISIBILITY?.BUTTONS?.SPOT_SHARE?.MOBILE ?? true)
            : (UI_VISIBILITY?.BUTTONS?.SPOT_SHARE?.DESKTOP ?? true);
        const showSpotShare = allowShare && !!spotShareSetting;
        const allowFavorite = isDynamicButtonEnabled('favorite') && !!currentSpot?.id;
        const coords = hasLatLng(currentSpot);
        const allowDirections = isDynamicButtonEnabled('directions') && !!coords;

        // Dynamic URL buttons logic
        const hasUrl1 = currentSpot?.url_1;
        const hasUrl2 = currentSpot?.url_2;
        // Respect UI visibility flags for the new spot URL buttons (desktop/mobile)
        const url1Setting = mobile
            ? (UI_VISIBILITY?.BUTTONS?.SPOT_URL_1?.MOBILE ?? true)
            : (UI_VISIBILITY?.BUTTONS?.SPOT_URL_1?.DESKTOP ?? true);
        const url2Setting = mobile
            ? (UI_VISIBILITY?.BUTTONS?.SPOT_URL_2?.MOBILE ?? true)
            : (UI_VISIBILITY?.BUTTONS?.SPOT_URL_2?.DESKTOP ?? true);

        const showUrl1 = !!hasUrl1 && !!url1Setting;
        const showUrl2 = !!hasUrl2 && !!url2Setting;

        // Icon fallback logic
        const url1Icon = currentSpot?.url_1_icon ? currentSpot.url_1_icon : 'globe-solid-full';
        const url2Icon = currentSpot?.url_2_icon ? currentSpot.url_2_icon : 'globe-solid-full';

        if (spotUrlBtn1) {
            spotUrlBtn1.style.display = showUrl1 ? '' : 'none';
            spotUrlBtn1.innerHTML = '';
            if (showUrl1) spotUrlBtn1.appendChild(createIconElement(url1Icon, 'icon'));
        }
        if (spotUrlBtn2) {
            spotUrlBtn2.style.display = showUrl2 ? '' : 'none';
            spotUrlBtn2.innerHTML = '';
            if (showUrl2) spotUrlBtn2.appendChild(createIconElement(url2Icon, 'icon'));
        }

        // Hide individual buttons if not allowed
        if (spotShareBtn) spotShareBtn.style.display = showSpotShare ? '' : 'none';
        if (spotFavBtn) spotFavBtn.style.display = allowFavorite ? '' : 'none';
        if (spotDirectionsBtn) spotDirectionsBtn.style.display = allowDirections ? '' : 'none';

        const anyVisible = showSpotShare || allowFavorite || allowDirections || showUrl1 || showUrl2;
        spotActionsContainer.style.display = (spotActionsEnabled && anyVisible) ? '' : 'none';

        // If no actions are visible, fall back to default controls
        if (!anyVisible && spotActionsEnabled) {
            if (locationBtn) locationBtn.style.display = locationVisible ? '' : 'none';
            if (shareBtn) shareBtn.style.display = shareVisible ? '' : 'none';
        }
    }



    // (handlers moved to top-level scope)
}

// Top-level scope: handlers for spot URL buttons
function handleSpotUrl1() {
    if (!currentSpot || !currentSpot.url_1) return;
    window.open(currentSpot.url_1, '_blank', 'noopener');
}

function handleSpotUrl2() {
    if (!currentSpot || !currentSpot.url_2) return;
    window.open(currentSpot.url_2, '_blank', 'noopener');
}
function syncFavoriteButtonState(favoritesDetail) {
    if (!spotFavBtn || !currentSpot?.id) return;
    const currentId = parseInt(currentSpot.id);
    const ids = Array.isArray(favoritesDetail) ? favoritesDetail.map(id => parseInt(id)) : null;
    const isFavState = ids ? ids.includes(currentId) : isFavorite(currentId);
    spotFavBtn.classList.toggle('active', !!isFavState);
}

export async function initializeMapButtons() {
    // Ensure static controls/groups exist from config, then create dynamic spot actions
    ensureControlsFromConfig();
    // Re-query DOM elements in case they were created above
    zoomInBtn = document.getElementById('zoom-in');
    zoomOutBtn = document.getElementById('zoom-out');
    locationBtn = document.getElementById('location');
    shareBtn = document.getElementById('share');
    loadingIndicator = document.getElementById('loading');
    mapControlsGroup = document.querySelector('.map-controls-group');

    // Create spot-actions container if enabled
    ensureSpotActions();

    // Apply initial button visibility
    applyButtonVisibility();

    // Reapply visibility on window resize
    window.addEventListener('resize', applyButtonVisibility);

    // Add accessibility labels to buttons
    if (zoomInBtn) {
        const zoomInLabel = await getText('buttons.zoomIn') || 'Zoom in';
        zoomInBtn.setAttribute('aria-label', zoomInLabel);
        if (!zoomInBtn.getAttribute('title')) {
            zoomInBtn.setAttribute('title', zoomInLabel);
        }
    }

    if (zoomOutBtn) {
        const zoomOutLabel = await getText('buttons.zoomOut') || 'Zoom out';
        zoomOutBtn.setAttribute('aria-label', zoomOutLabel);
        if (!zoomOutBtn.getAttribute('title')) {
            zoomOutBtn.setAttribute('title', zoomOutLabel);
        }
    }

    if (locationBtn) {
        const locationLabel = await getText('buttons.location') || 'My location';
        locationBtn.setAttribute('aria-label', locationLabel);
        if (!locationBtn.getAttribute('title')) {
            locationBtn.setAttribute('title', locationLabel);
        }
    }

    if (shareBtn) {
        const shareLabel = await getText('buttons.share') || 'Share';
        shareBtn.setAttribute('aria-label', shareLabel);
        // Keep existing title if present, otherwise add it
        if (!shareBtn.getAttribute('title')) {
            shareBtn.setAttribute('title', shareLabel);
        }
    }

    if (loadingIndicator) {
        const loadingTextEl = loadingIndicator.querySelector('.location-loading-text');
        if (loadingTextEl) {
            const gettingLocationText = await getText('messages.gettingLocation');
            loadingTextEl.textContent = gettingLocationText || 'Getting your location...';
        }
    }

    // Spot action button labels (if enabled)
    if (spotActionsContainer) {
        const [shareLabel, favLabel, directionsLabel] = await Promise.all([
            getText('buttons.share'),
            getText('buttons.favorites'),
            getText('buttons.directions')
        ]);
        if (spotShareBtn) spotShareBtn.setAttribute('aria-label', shareLabel || 'Share spot');
        if (spotFavBtn) spotFavBtn.setAttribute('aria-label', favLabel || 'Save favorite');
        if (spotDirectionsBtn) spotDirectionsBtn.setAttribute('aria-label', directionsLabel || 'Get directions');
    }

    // Zoom controls
    zoomInBtn.addEventListener('click', handleZoomIn);
    zoomOutBtn.addEventListener('click', handleZoomOut);

    // Location button
    locationBtn.addEventListener('click', handleLocationClick);

    // Smart Share button
    shareBtn.addEventListener('click', handleSmartShare);

    // Favorites state syncing
    window.addEventListener('favoritesChanged', (e) => syncFavoriteButtonState(e?.detail));

    // Listen for spot selection events (from cards/markers)
    window.addEventListener('spotSelected', handleSpotSelected);
    window.addEventListener('spotCleared', handleSpotCleared);
}

function handleZoomIn() {
    const map = getMap();
    map.zoomIn();
}

function handleZoomOut() {
    const map = getMap();
    map.zoomOut();
}

function handleLocationClick() {
    const map = getMap();
    if (loadingIndicator) {
        loadingIndicator.style.display = 'flex';
        requestAnimationFrame(() => loadingIndicator.classList.add('show'));
    }
    getAndStoreUserLocation()
        .then(({ latitude, longitude }) => {
            if (loadingIndicator) {
                loadingIndicator.classList.remove('show');
                setTimeout(() => {
                    loadingIndicator.style.display = 'none';
                }, 200);
            }

            const userLatLng = L.latLng(latitude, longitude);
            const maxBounds = map.options.maxBounds;

            // Check if the user's location is outside the defined map bounds
            if (maxBounds && !maxBounds.contains(userLatLng)) {
                alert('Your current location is outside the map area.');
                return; // Stop execution and do not move the map
            }

            // Set location marker
            setLocationMarker([latitude, longitude]);
            // Center map on location
            map.setView([latitude, longitude], 13);
            // Dispatch event to update popups
            window.dispatchEvent(new Event('userLocationChanged'));
        })
        .catch(err => {
            if (loadingIndicator) {
                loadingIndicator.classList.remove('show');
                setTimeout(() => {
                    loadingIndicator.style.display = 'none';
                }, 200);
            }
            console.error('[map-controls] Error getting location:', err);
            alert('Error getting your location. Please try again.');
        });
}

function handleSmartShare() {
    const currentUrl = window.location.href;
    openSmartSharePopup(currentUrl);
}

async function handleSpotSelected(event) {
    if (!MAP_DYNAMIC_ACTION_BUTTONS?.ENABLED) return;
    const detail = event?.detail || {};
    const spot = detail.spot || null;
    if (!spot) return;
    // Use the full spot object from marker.js as the source of truth
    currentSpot = { ...spot };
    syncFavoriteButtonState();
    applyButtonVisibility();
}

function handleSpotCleared() {
    if (!MAP_DYNAMIC_ACTION_BUTTONS?.ENABLED) return;
    currentSpot = null;
    if (spotFavBtn) {
        spotFavBtn.classList.remove('active');
    }
    applyButtonVisibility();
}

function handleSpotShare() {
    if (!currentSpot || !currentSpot.id) return;
    // Pass the full spot object for sharing (future extensibility, e.g. cid_googleMaps)
    openSharePopup({
        spotId: currentSpot.id,
        spotTitle: currentSpot.title || '',
        spot: currentSpot
    });
}

async function handleSpotFavorite() {
    if (!currentSpot || !currentSpot.id) return;
    // Pass the full spot object for favorites (future extensibility)
    await toggleFavorite(currentSpot.id, currentSpot);
    syncFavoriteButtonState();
}

function handleSpotDirections() {
    if (!currentSpot) return;
    const coords = hasLatLng(currentSpot);
    if (!coords) return;
    // Always pass the full spot object for future-proofing, but keep current API for now
    showDirectionsPopup({
        lat: coords.lat,
        lng: coords.lng,
        cid_googleMaps: currentSpot?.cid_googleMaps,
        spot: currentSpot // Pass the whole spot for extensibility
    });
}

export function createAnimatedLocationMarker(latLng) {
    return L.marker(latLng, {
        icon: L.divIcon({
            className: 'location-animated-marker',
            iconSize: [28, 28],
            iconAnchor: [14, 14],
            popupAnchor: [0, -14]
        })
    });
} 